import classNames from 'classnames';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import Slider from 'react-slick';

import SliderNextArrow from './SliderNextArrow';
import SliderPreviousArrow from './SliderPreviousArrow';

/**
 * Own slick structure only (never nested carousels inside slides):
 * .Slider > .slick-slider|.slick-initialized > .slick-list > .slick-track > .slick-slide
 */
const getOwnSlickList = (containerEl) => {
  if (!containerEl) return null;

  return (
    containerEl.querySelector(':scope > .slick-slider > .slick-list') ||
    containerEl.querySelector(':scope > .slick-initialized > .slick-list')
  );
};

const getOwnSlickSlides = (containerEl) => {
  const slickList = getOwnSlickList(containerEl);
  if (!slickList) return [];

  const track = slickList.querySelector(':scope > .slick-track');
  if (!track) return [];

  return Array.prototype.filter.call(track.children, (child) =>
    child.classList.contains('slick-slide')
  );
};

const updateSlideInertState = (containerEl) => {
  if (!containerEl) return;

  getOwnSlickSlides(containerEl).forEach((slide) => {
    if (slide.getAttribute('aria-hidden') === 'true') {
      slide.setAttribute('inert', '');
    } else {
      slide.removeAttribute('inert');
    }
  });
};

const rectsOverlap = (a, b) =>
  !(
    a.right <= b.left ||
    a.left >= b.right ||
    a.bottom <= b.top ||
    a.top >= b.bottom
  );

// Any overlap with this instance's .slick-list keeps the slide interactive.
// Prefer rects over IntersectionObserver: slick moves slides via CSS transform,
// which often does not re-deliver IO callbacks after arrow navigation.
// Returns true when inert was updated; false when list is missing or not laid out.
const updateSlideInertByGeometry = (containerEl) => {
  if (!containerEl) return false;

  const slickList = getOwnSlickList(containerEl);
  if (!slickList) return false;

  const listRect = slickList.getBoundingClientRect();
  if (listRect.width === 0 && listRect.height === 0) return false;

  getOwnSlickSlides(containerEl).forEach((slide) => {
    if (rectsOverlap(slide.getBoundingClientRect(), listRect)) {
      slide.removeAttribute('inert');
    } else {
      slide.setAttribute('inert', '');
    }
  });
  return true;
};

const CommonSlider = forwardRef((props, ref) => {
  const {
    className,
    dots,
    children,
    pauseOnHover,
    prevArrow: PrevArrow = SliderPreviousArrow,
    nextArrow: NextArrow = SliderNextArrow,
    arrows,
    infinite,
    autoplay,
    magnified,
    isThumbnailSlider,
    slides,
    centerMode,
    variableWidth,
    onMount,
    beforeChange,
    afterChange,
    geometryBasedInert = false,
    ...rest
  } = props;

  const sliderRef = useRef(null);
  const innerSliderRef = useRef(null);
  const containerRef = useRef(null);
  const lastOnMountRef = useRef(null);

  const handleBeforeChange = useCallback(
    (...args) => {
      const container = containerRef.current;
      if (container && container.contains(document.activeElement)) {
        document.activeElement.blur();
      }

      if (beforeChange) {
        beforeChange(...args);
      }
    },
    [beforeChange]
  );

  const handleAfterChange = useCallback(
    (...args) => {
      if (geometryBasedInert) {
        updateSlideInertByGeometry(containerRef.current);
      }

      if (afterChange) {
        afterChange(...args);
      }
    },
    [afterChange, geometryBasedInert]
  );

  /**
   * onMount is called from the ref callback with { innerRef, slideCount } so the parent
   * can call methods on the real slider instance (e.g. innerRef.slickGoTo.call(innerRef, index)).
   */

  /**
   * Inert sync (own slick track/list only — never nested carousels inside slides):
   * - Default: mirror aria-hidden → inert on own slides (Lighthouse: focusable inside aria-hidden).
   * - geometryBasedInert: any getBoundingClientRect overlap between an own slide and this
   *   instance’s .slick-list keeps the slide interactive; otherwise set inert. Refresh after
   *   layout (double rAF, with one deferred retry if the list has no size yet) and on
   *   afterChange. Do not drive this instance from aria-hidden while geometry mode is on.
   */
  useEffect(() => {
    if (geometryBasedInert) {
      return;
    }

    const container = containerRef.current;
    if (!container || typeof MutationObserver === 'undefined') return;

    updateSlideInertState(container);

    const observer = new MutationObserver((mutations) => {
      const ownSlides = new Set(getOwnSlickSlides(container));
      if (mutations.some((mutation) => ownSlides.has(mutation.target))) {
        updateSlideInertState(container);
      }
    });

    observer.observe(container, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      subtree: true,
    });

    return () => observer.disconnect();
  }, [geometryBasedInert]);

  useEffect(() => {
    if (!geometryBasedInert) return;

    const container = containerRef.current;
    if (!container) return;

    const pendingRafs = [];

    const scheduleDoubleRaf = (callback) => {
      let innerId = 0;
      const outerId = requestAnimationFrame(() => {
        innerId = requestAnimationFrame(callback);
        pendingRafs.push(innerId);
      });
      pendingRafs.push(outerId);
    };

    // Wait for slick layout; if the list still has no size, retry once.
    scheduleDoubleRaf(() => {
      if (!updateSlideInertByGeometry(container)) {
        scheduleDoubleRaf(() => {
          updateSlideInertByGeometry(container);
        });
      }
    });

    return () => {
      pendingRafs.forEach((id) => cancelAnimationFrame(id));
    };
  }, [geometryBasedInert]);

  const getVariableWidth = () => {
    if (getSlides().length === 1 && centerMode) {
      return false;
    }

    return variableWidth;
  };

  const getSlides = useCallback(() => {
    if (slides) return slides;

    return children;
  }, [slides, children]);

  const slider = (
    <Slider
      ref={(innerRef) => {
        innerSliderRef.current = innerRef;
        const slideCount = getSlides().length;
        sliderRef.current = {
          ...innerRef,
          slideCount,
        };

        if (ref) {
          ref.current = innerRef;
        }

        // Defer onMount so parent setState doesn't run during ref callback (avoids update depth loop)
        if (onMount && innerRef) {
          if (lastOnMountRef.current !== innerRef) {
            lastOnMountRef.current = innerRef;
            const defer =
              typeof queueMicrotask === 'function'
                ? queueMicrotask
                : (cb) => Promise.resolve().then(cb);
            defer(() => onMount({ innerRef, slideCount }));
          }
        } else if (!innerRef) {
          lastOnMountRef.current = null;
        }
      }}
      pauseOnHover={pauseOnHover || true}
      infinite={infinite || false}
      nextArrow={
        <NextArrow
          elementClassName={className ? `${className}__arrow` : ''}
          infinite={infinite}
          parentRef={sliderRef}
          innerSliderRef={innerSliderRef}
          isThumbnailSlider={isThumbnailSlider}
        />
      }
      prevArrow={
        <PrevArrow
          elementClassName={className ? `${className}__arrow` : ''}
          infinite={infinite}
          parentRef={sliderRef}
          innerSliderRef={innerSliderRef}
          isThumbnailSlider={isThumbnailSlider}
        />
      }
      arrows={(arrows && getSlides().length > 1) || false}
      autoplay={(autoplay && getSlides().length > 1) || false}
      // centerMode must not depend on variableWidth — main/footer BOTH/CENTERED
      // pass centerMode while full-bleed creatives (>=1900px) set variableWidth
      // false; gating on variableWidth silently disabled centering.
      centerMode={(centerMode && getSlides().length > 1) || false}
      variableWidth={getVariableWidth()}
      dots={(dots && getSlides().length > 1) || false}
      {...rest}
      beforeChange={handleBeforeChange}
      afterChange={handleAfterChange}
    >
      {getSlides()}
    </Slider>
  );

  return (
    <div
      ref={containerRef}
      className={classNames(
        'Slider',
        {
          'Slider--with-dots': dots,
          'Slider--full-width': getSlides().length === 1 && !centerMode,
          'Slider--magnified': magnified,
        },
        className
      )}
    >
      {slider}
    </div>
  );
});

CommonSlider.propTypes = {
  className: PropTypes.string,
  arrows: PropTypes.bool,
  autoplay: PropTypes.bool,
  centerMode: PropTypes.bool,
  dots: PropTypes.bool,
  infinite: PropTypes.bool,
  isThumbnailSlider: PropTypes.bool,
  magnified: PropTypes.bool,
  pauseOnHover: PropTypes.bool,
  variableWidth: PropTypes.bool,
  geometryBasedInert: PropTypes.bool,
  slides: PropTypes.array,
  nextArrow: PropTypes.object,
  prevArrow: PropTypes.object,
  onMount: PropTypes.func,
};

export default observer(CommonSlider);
